<script setup lang="ts">
import { ThumbsUp, ThumbsDown, Share2, Bookmark } from 'lucide-vue-next'

interface Props {
  title: string
  description: string
  subject?: {
    name: string
    color?: string
  }
  level?: {
    name: string
  }
  isNew?: boolean
  isFree?: boolean
  publishedAt: string
  viewCount: number
  likes: number
  dislikes: number
  shares: number
}

const props = withDefaults(defineProps<Props>(), {
  isNew: false,
  isFree: false,
})

const formatViews = (count: number) => {
  if (!count) return '0'
  if (count >= 1000) {
    return (count / 1000).toFixed(1) + 'K'
  }
  return count.toString()
}

const formatTimeAgo = (date: string) => {
  const now = new Date()
  const then = new Date(date)
  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`
  return 'Published ' + then.toLocaleDateString()
}
</script>

<template>
  <section id="video-info" class="bg-white rounded-xl border border-gray-200 p-6">
    <div class="flex items-start justify-between mb-4">
      <div class="flex-1">
        <div class="flex items-center gap-2 mb-3">
          <span
            v-if="subject"
            class="bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded"
          >
            {{ subject.name }}
          </span>
          <span v-if="isNew" class="bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-1 rounded">
            NEW
          </span>
          <span v-if="isFree" class="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded">
            FREE
          </span>
          <span v-if="level" class="text-gray-500 text-sm">{{ level.name }}</span>
          <span class="text-gray-500 text-sm">• {{ formatTimeAgo(publishedAt) }}</span>
        </div>
        <h2 class="text-2xl font-bold text-gray-900 mb-2">{{ title }}</h2>
        <p class="text-gray-600 leading-relaxed">{{ description }}</p>
      </div>
    </div>

    <!-- Like/Dislike/Share/Save controls - hidden for now, will bring back later
    <div class="flex items-center justify-between pt-4 border-t border-gray-200">
      <div class="flex items-center gap-6">
        <button class="flex items-center gap-2 text-gray-700 hover:text-brand-blue transition-colors group">
          <div
            class="w-10 h-10 bg-gray-100 group-hover:bg-blue-50 rounded-full flex items-center justify-center transition-colors"
          >
            <ThumbsUp class="w-5 h-5" />
          </div>
          <div class="text-left">
            <p class="text-sm font-semibold">Like</p>
            <p class="text-xs text-gray-500">{{ likes }}</p>
          </div>
        </button>
        <button class="flex items-center gap-2 text-gray-700 hover:text-red-500 transition-colors group">
          <div
            class="w-10 h-10 bg-gray-100 group-hover:bg-red-50 rounded-full flex items-center justify-center transition-colors"
          >
            <ThumbsDown class="w-5 h-5" />
          </div>
          <div class="text-left">
            <p class="text-sm font-semibold">Dislike</p>
            <p class="text-xs text-gray-500">{{ dislikes }}</p>
          </div>
        </button>
        <button class="flex items-center gap-2 text-gray-700 hover:text-brand-purple transition-colors group">
          <div
            class="w-10 h-10 bg-gray-100 group-hover:bg-purple-50 rounded-full flex items-center justify-center transition-colors"
          >
            <Share2 class="w-5 h-5" />
          </div>
          <div class="text-left">
            <p class="text-sm font-semibold">Share</p>
            <p class="text-xs text-gray-500">{{ shares }}</p>
          </div>
        </button>
        <button class="flex items-center gap-2 text-gray-700 hover:text-brand-green transition-colors group">
          <div
            class="w-10 h-10 bg-gray-100 group-hover:bg-green-50 rounded-full flex items-center justify-center transition-colors"
          >
            <Bookmark class="w-5 h-5" />
          </div>
          <div class="text-left">
            <p class="text-sm font-semibold">Save</p>
          </div>
        </button>
      </div>
      <div class="flex items-center gap-3">
        <div class="text-right">
          <p class="text-sm font-semibold text-gray-900">{{ formatViews(viewCount) }} views</p>
          <p class="text-xs text-gray-500">Last 24 hours</p>
        </div>
      </div>
    </div>
    -->
  </section>
</template>
